import React, { useEffect, useMemo, useState } from "react";
import api from "../../config/axios";
import "./Orders.css";
import LoadingSpinner from "../common/LoadingSpinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Select } from "../ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  MdCheckCircle,
  MdClose,
  MdDownload,
  MdRefresh,
  MdSearch,
  MdVisibility,
} from "react-icons/md";

const EMPTY_IMAGE =
  "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=56&h=56&fit=crop";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency: "GHS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(amount));

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-GH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const calcChange = (current, previous = 0) => {
  const cur = toNumber(current);
  const prev = toNumber(previous);
  if (prev <= 0) return cur > 0 ? "15.0" : "0.0";
  return (((cur - prev) / prev) * 100).toFixed(1);
};

const getStatusText = (paid, deliveryStatus) => {
  if (deliveryStatus === "DELIVERED" || deliveryStatus === "PICKED_UP") return "Delivered";
  if (deliveryStatus === "IN_TRANSIT") return "In Transit";
  if (paid) return "Paid";
  return "Pending";
};

const getStatusVariant = (paid, deliveryStatus) => {
  if (deliveryStatus === "DELIVERED" || deliveryStatus === "PICKED_UP") return "success";
  if (deliveryStatus === "IN_TRANSIT") return "warning";
  if (paid) return "info";
  return "secondary";
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState({});
  const [vendors, setVendors] = useState({});
  const [buyers, setBuyers] = useState({});
  const [analytics, setAnalytics] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDeliveryType, setFilterDeliveryType] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    title: "",
    comment: "",
    recommendToOthers: true,
  });

  const getProductInfo = (itemId) => {
    const product = products[itemId];
    if (!product) {
      return { name: "Product Not Found", image: EMPTY_IMAGE, price: 0 };
    }
    return {
      name: product.name || "Unknown Product",
      image: product.image || EMPTY_IMAGE,
      price: toNumber(product.price),
    };
  };

  const getVendorInfo = (vendorId) => {
    const vendor = vendors[vendorId];
    if (!vendor) {
      return { name: "Vendor Not Found", businessName: "Unknown Business", email: "N/A" };
    }
    return {
      name: `${vendor.firstName || "Unknown"} ${vendor.lastName || "Vendor"}`.trim(),
      businessName: vendor.businessName || "No Business Name",
      email: vendor.email || "N/A",
    };
  };

  const getBuyerInfo = (buyerId) => {
    const buyer = buyers[buyerId];
    if (!buyer) return { name: "Buyer Not Found", email: "N/A" };
    return {
      name: `${buyer.firstName || "Unknown"} ${buyer.lastName || "Buyer"}`.trim(),
      email: buyer.email || "N/A",
    };
  };

  const fetchOrders = async () => {
    const response = await api.get("/admin/orders");
    setOrders(response.data?.data || response.data || []);
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get("/admin/orders/analytics");
      setAnalytics(response.data?.data || response.data || null);
    } catch (_error) {
      setAnalytics(null);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("/cart/market");
      const productsMap = {};
      (response.data?.data || response.data || []).forEach((product) => {
        const key = product.id ?? product.Id ?? product.ID;
        if (key) productsMap[key] = product;
      });
      setProducts(productsMap);
    } catch (_error) {
      setProducts({});
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get("/admin/vendors");
      const map = {};
      (response.data?.data || []).forEach((vendor) => {
        map[vendor.id] = vendor;
      });
      setVendors(map);
    } catch (_error) {
      setVendors({});
    }
  };

  const fetchBuyers = async () => {
    try {
      const response = await api.get("/admin/users");
      const map = {};
      (response.data?.data || []).forEach((buyer) => {
        map[buyer.id] = buyer;
      });
      setBuyers(map);
    } catch (_error) {
      setBuyers({});
    }
  };

  const fetchAllData = async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    if (background) setRefreshing(true);
    setError("");
    try {
      await Promise.all([
        fetchOrders(),
        fetchProducts(),
        fetchVendors(),
        fetchBuyers(),
        fetchAnalytics(),
      ]);
    } catch (_err) {
      setError("Failed to load orders data. Please try again.");
    } finally {
      if (!background) setLoading(false);
      if (background) setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const product = getProductInfo(order.itemId);
      const vendor = getVendorInfo(order.vendorId);
      const buyer = getBuyerInfo(order.buyerId);
      const statusText = getStatusText(order.paid, order.deliveryStatus).toLowerCase();

      const matchesSearch =
        String(order?.id || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        String(order?.referenceId || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyer.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "paid" && order.paid) ||
        (filterStatus === "pending" && !order.paid) ||
        (filterStatus === "delivered" &&
          (order.deliveryStatus === "DELIVERED" || order.deliveryStatus === "PICKED_UP")) ||
        (filterStatus === "in_transit" && order.deliveryStatus === "IN_TRANSIT") ||
        statusText === filterStatus;

      const matchesDeliveryType =
        filterDeliveryType === "all" || order.deliveryType === filterDeliveryType;

      return matchesSearch && matchesStatus && matchesDeliveryType;
    });
  }, [orders, searchTerm, filterStatus, filterDeliveryType, products, vendors, buyers]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const handleStatusUpdate = async (orderId, newPaidStatus) => {
    try {
      setActionLoading(true);
      await api.put(`/admin/orders/${orderId}/status`, { paid: newPaidStatus });
      await fetchAllData({ background: true });
    } catch (_err) {
      alert("Failed to update payment status.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeliveryStatusUpdate = async (orderId, newDeliveryStatus) => {
    try {
      setActionLoading(true);
      await api.put(`/admin/orders/${orderId}/delivery-status`, null, {
        params: { deliveryStatus: newDeliveryStatus, adminNotes: "Updated by admin" },
      });
      await fetchAllData({ background: true });
    } catch (_err) {
      alert("Failed to update delivery status.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportOrders = async () => {
    try {
      setExportLoading(true);
      const headers = [
        "Order ID",
        "Reference ID",
        "Product",
        "Vendor",
        "Buyer",
        "Amount",
        "Quantity",
        "Delivery Type",
        "Status",
        "Created Date",
      ];
      const csvContent = [
        headers.join(","),
        ...filteredOrders.map((order) => {
          const product = getProductInfo(order.itemId);
          const vendor = getVendorInfo(order.vendorId);
          const buyer = getBuyerInfo(order.buyerId);
          return [
            order.id,
            order.referenceId || "N/A",
            `"${product.name}"`,
            `"${vendor.name}"`,
            `"${buyer.name}"`,
            toNumber(order.amountToBePaid),
            toNumber(order.quantity),
            order.deliveryType || "N/A",
            getStatusText(order.paid, order.deliveryStatus),
            formatDate(order.createdAt),
          ].join(",");
        }),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-export-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (_err) {
      alert("Failed to export orders.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedOrder) return;
    if (toNumber(reviewData.rating) <= 0 || !reviewData.title.trim() || !reviewData.comment.trim()) {
      alert("Rating, title and comment are required.");
      return;
    }
    try {
      setReviewLoading(true);
      await api.post("/admin/orders/review", {
        orderId: selectedOrder.id,
        productId: selectedOrder.itemId,
        vendorId: selectedOrder.vendorId,
        rating: toNumber(reviewData.rating),
        title: reviewData.title.trim(),
        comment: reviewData.comment.trim(),
        recommendToOthers: !!reviewData.recommendToOthers,
        reviewDate: new Date().toISOString(),
      });
      setShowReviewModal(false);
      setReviewData({ rating: 0, title: "", comment: "", recommendToOthers: true });
    } catch (_err) {
      alert("Failed to submit review.");
    } finally {
      setReviewLoading(false);
    }
  };

  const analyticsItems = [
    { label: "Total Orders", value: analytics?.totalOrders || 0, change: calcChange(analytics?.totalOrders) },
    { label: "Paid Orders", value: analytics?.paidOrders || 0, change: calcChange(analytics?.paidOrders) },
    { label: "Pending Orders", value: analytics?.pendingOrders || 0, change: calcChange(analytics?.pendingOrders) },
    {
      label: "Ready for Pickup",
      value: analytics?.readyForPickupOrders || 0,
      change: calcChange(analytics?.readyForPickupOrders),
    },
  ];

  if (loading) {
    return (
      <div className="orders-page">
        <Card>
          <CardHeader>
            <CardTitle>Orders</CardTitle>
            <CardDescription>Loading orders and related data...</CardDescription>
          </CardHeader>
          <CardContent>
            <LoadingSpinner size="large" message="Loading orders..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <Card>
        <CardHeader className="orders-header-row">
          <div>
            <CardTitle>Orders</CardTitle>
            <CardDescription>Manage and monitor all customer orders</CardDescription>
          </div>
          <div className="orders-actions">
            <Button variant="outline" onClick={() => fetchAllData({ background: true })} disabled={refreshing}>
              <MdRefresh /> {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
            <Button onClick={handleExportOrders} disabled={exportLoading}>
              <MdDownload /> {exportLoading ? "Exporting..." : "Export CSV"}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {error ? (
        <Card>
          <CardContent className="orders-error">
            <p>{error}</p>
            <Button variant="outline" onClick={() => fetchAllData()}>
              <MdRefresh /> Retry
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {analytics ? (
        <div className="orders-analytics-grid">
          {analyticsItems.map((item) => (
            <Card key={item.label}>
              <CardHeader>
                <CardDescription>{item.label}</CardDescription>
                <CardTitle>{toNumber(item.value).toLocaleString("en-GH")}</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant={toNumber(item.change) >= 0 ? "success" : "destructive"}>
                  {toNumber(item.change) >= 0 ? "+" : ""}
                  {item.change}%
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardContent className="orders-filters">
          <div className="orders-search">
            <MdSearch />
            <Input
              type="search"
              placeholder="Search by order, product, vendor, buyer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="delivered">Delivered</option>
            <option value="in_transit">In Transit</option>
          </Select>
          <Select
            value={filterDeliveryType}
            onChange={(e) => setFilterDeliveryType(e.target.value)}
          >
            <option value="all">All Delivery Types</option>
            <option value="KEEJANI_DELIVERY">Keejani Delivery</option>
            <option value="BUYER_PICKUP">Buyer Pickup</option>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="orders-table-wrap">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="orders-empty-row">
                    No orders found for current filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrders.map((order, index) => {
                  const product = getProductInfo(order.itemId);
                  const vendor = getVendorInfo(order.vendorId);
                  const buyer = getBuyerInfo(order.buyerId);
                  return (
                    <TableRow key={order?.id ?? order?.referenceId ?? `order-${index}`}>
                      <TableCell>
                        <div className="order-id-cell">
                          <strong>#{String(order?.id || "N/A").slice(-6)}</strong>
                          <small>{order?.referenceId || "N/A"}</small>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="orders-product-cell">
                          <img
                            src={product.image}
                            alt={product.name}
                            onError={(e) => {
                              e.currentTarget.src = EMPTY_IMAGE;
                            }}
                          />
                          <div>
                            <div>{product.name}</div>
                            <small>{formatCurrency(product.price)}</small>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="orders-name-block">
                          <span>{vendor.name}</span>
                          <small>{vendor.businessName}</small>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="orders-name-block">
                          <span>{order?.buyerName || buyer.name}</span>
                          <small>{buyer.email}</small>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="orders-name-block">
                          <span>{formatCurrency(order?.amountToBePaid)}</span>
                          <small>Qty: {toNumber(order?.quantity)}</small>
                        </div>
                      </TableCell>
                      <TableCell>{order?.deliveryType || "N/A"}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(order?.paid, order?.deliveryStatus)}>
                          {getStatusText(order?.paid, order?.deliveryStatus)}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(order?.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="orders-row-actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderDetails(true);
                            }}
                          >
                            <MdVisibility />
                          </Button>
                          {!order?.paid ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={actionLoading}
                              onClick={() => handleStatusUpdate(order.id, true)}
                            >
                              Paid
                            </Button>
                          ) : null}
                          {order?.pickupStatus === "READY_FOR_PICKUP" ? (
                            <Button
                              size="sm"
                              disabled={actionLoading}
                              onClick={() => handleDeliveryStatusUpdate(order.id, "PICKED_UP")}
                            >
                              <MdCheckCircle /> Picked
                            </Button>
                          ) : null}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowReviewModal(true);
                            }}
                          >
                            Review
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="orders-pagination">
        <Button
          variant="outline"
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
        >
          Previous
        </Button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <Button
          variant="outline"
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
      </div>

      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder ? (
            <div className="orders-dialog-body">
              <div>
                <Label>Order ID</Label>
                <p>#{selectedOrder.id}</p>
              </div>
              <div>
                <Label>Reference</Label>
                <p>{selectedOrder.referenceId || "N/A"}</p>
              </div>
              <div>
                <Label>Amount</Label>
                <p>{formatCurrency(selectedOrder.amountToBePaid)}</p>
              </div>
              <div>
                <Label>Status</Label>
                <Badge variant={getStatusVariant(selectedOrder.paid, selectedOrder.deliveryStatus)}>
                  {getStatusText(selectedOrder.paid, selectedOrder.deliveryStatus)}
                </Badge>
              </div>
              <div>
                <Label>Delivery</Label>
                <p>{selectedOrder.deliveryType || "N/A"}</p>
              </div>
              <div>
                <Label>Date</Label>
                <p>{formatDate(selectedOrder.createdAt)}</p>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDetails(false)}>
              <MdClose /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Review</DialogTitle>
          </DialogHeader>
          <div className="orders-dialog-body">
            <div>
              <Label htmlFor="review-rating">Rating (1-5)</Label>
              <Select
                id="review-rating"
                value={String(reviewData.rating)}
                onChange={(e) =>
                  setReviewData((prev) => ({ ...prev, rating: toNumber(e.target.value) }))
                }
              >
                <option value="0">Select rating</option>
                <option value="1">1 - Poor</option>
                <option value="2">2 - Fair</option>
                <option value="3">3 - Good</option>
                <option value="4">4 - Very Good</option>
                <option value="5">5 - Excellent</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="review-title">Title</Label>
              <Input
                id="review-title"
                value={reviewData.title}
                onChange={(e) => setReviewData((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Short review title"
              />
            </div>
            <div>
              <Label htmlFor="review-comment">Comment</Label>
              <Textarea
                id="review-comment"
                value={reviewData.comment}
                onChange={(e) => setReviewData((prev) => ({ ...prev, comment: e.target.value }))}
                placeholder="Share your review"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitReview} disabled={reviewLoading}>
              {reviewLoading ? "Submitting..." : "Submit Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
